import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, MinusCircle } from 'lucide-react';

const ANSWER_OPTIONS = [
  { value: 'OK', label: 'OK' },
  { value: 'Not OK', label: 'Not OK' },
  { value: 'N/A', label: 'N/A' },
];

interface ChecklistItem {
  id: string;
  task: string | null;
  answer: string | null;
  comments: string | null;
}

function AnswerIcon({ answer }: { answer: string | null }) {
  if (answer === 'OK') return <CheckCircle2 className="h-4 w-4 text-green-600" />;
  if (answer === 'Not OK') return <XCircle className="h-4 w-4 text-destructive" />;
  if (answer === 'N/A') return <MinusCircle className="h-4 w-4 text-muted-foreground" />;
  return null;
}

/** Imperative handle exposed by `JobCardChecklist` when `deferCommit` is on.
 *  Lets the parent flush draft edits to the server in one shot (mirrors the
 *  pattern used by `ServiceDetailsForm` on the View Service page). */
export interface JobCardChecklistHandle {
  /** Persist every row whose UI value diverges from the server-known value.
   *  Resolves once all PUTs settle. */
  flush: () => Promise<void>;
}

/**
 * Inline-editable checklist for a JobCard. Each row autosaves on commit
 * (Answer change or Comments blur). Ideal for techs to fill in as they work.
 *
 * Pass `deferCommit` to suppress the per-row autosave — used by the past-
 * service flow, which is all-or-nothing on the single Submit. In defer mode
 * the row still feels live (optimistic cache writes drive the displayed
 * answer + the parent's progress count), but no PUTs leave the client until
 * the parent calls `flush()` on the imperative handle.
 */
export const JobCardChecklist = React.forwardRef<JobCardChecklistHandle, {
  jobCardId: string;
  readOnly?: boolean;
  showProgress?: boolean;
  /** Override copy shown when the schedule has no checklist items.
   *  Past-service flow passes a context-appropriate message (the live
   *  flow's default still nudges the tech to hit Start). */
  emptyMessage?: string;
  /** When true, buffer answer / comment edits locally instead of PUTting
   *  them on commit. The parent flushes everything via the imperative
   *  handle. Default false keeps the live-flow autosave behaviour. */
  deferCommit?: boolean;
}>(function JobCardChecklist({
  jobCardId,
  readOnly = false,
  showProgress = true,
  emptyMessage,
  deferCommit = false,
}, ref) {
  const qc = useQueryClient();
  const queryKey = ['service-history-checklist-answers', 'jobCard', jobCardId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const r = await api<ChecklistItem[]>(`/service-history-checklist-answers?jobCard=${jobCardId}&limit=200`);
      return r.data || [];
    },
    enabled: !!jobCardId,
  });

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<ChecklistItem> }) => {
      const r = await api(`/service-history-checklist-answers/${id}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<ChecklistItem[]>(queryKey);
      qc.setQueryData<ChecklistItem[]>(queryKey, (old) =>
        (old || []).map((row) => (row.id === id ? { ...row, ...patch } : row)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKey, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey }),
  });

  // ---- Deferred-commit machinery ----
  // In defer mode we keep two parallel views of each row:
  //  - The React Query cache (mutated optimistically on every edit so the
  //    displayed value, AnswerIcon, progress badge, and the parent's
  //    `checklistUnanswered` count all stay live).
  //  - A `serverSnapshot` of the values as last fetched from the server.
  //    On flush() we diff the cache against this snapshot and PUT only the
  //    rows that actually changed.
  // The snapshot is captured on the first non-empty fetch and refreshed
  // whenever a successful flush settles (so a partial flush + further edits
  // can still diff cleanly).
  const serverSnapshotRef = React.useRef<Map<string, ChecklistItem> | null>(null);
  // Initialise the snapshot once the first fetch lands. We never reset it
  // mid-session in defer mode (would lose the diff baseline).
  React.useEffect(() => {
    if (!deferCommit) return;
    if (serverSnapshotRef.current) return;
    if (!data || data.length === 0) return;
    serverSnapshotRef.current = new Map(data.map((r) => [r.id, { ...r }]));
  }, [deferCommit, data]);

  // Apply an optimistic edit without firing the PUT. Used in defer mode
  // for both answer changes and comment blurs.
  function applyDraft(id: string, patch: Partial<ChecklistItem>) {
    qc.setQueryData<ChecklistItem[]>(queryKey, (old) =>
      (old || []).map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
  }

  React.useImperativeHandle(ref, () => ({
    async flush() {
      if (!deferCommit) return;
      const snapshot = serverSnapshotRef.current;
      const current = qc.getQueryData<ChecklistItem[]>(queryKey) ?? [];
      if (!snapshot) return;
      const pending: Array<Promise<unknown>> = [];
      for (const row of current) {
        const baseline = snapshot.get(row.id);
        if (!baseline) continue;
        const patch: Partial<ChecklistItem> = {};
        if ((row.answer ?? null) !== (baseline.answer ?? null)) patch.answer = row.answer ?? null;
        if ((row.comments ?? null) !== (baseline.comments ?? null)) patch.comments = row.comments ?? null;
        if (Object.keys(patch).length === 0) continue;
        pending.push(update.mutateAsync({ id: row.id, patch }));
      }
      if (pending.length === 0) return;
      await Promise.all(pending);
      // Refresh the baseline so any further edits in this session diff
      // against the new committed state.
      const after = qc.getQueryData<ChecklistItem[]>(queryKey) ?? [];
      serverSnapshotRef.current = new Map(after.map((r) => [r.id, { ...r }]));
    },
  }), [deferCommit, qc, queryKey, update]);

  const items = data || [];
  const answered = items.filter((i) => i.answer).length;
  const total = items.length;
  const pct = total === 0 ? 0 : Math.round((answered / total) * 100);

  if (isLoading) return <div className="text-xs text-muted-foreground py-3">Loading checklist…</div>;
  if (items.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4 px-3 border rounded-md">
        {emptyMessage ?? 'No checklist items yet — start the service to seed the checklist from the Service Plan Schedule.'}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {showProgress && (
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
          </div>
          <Badge variant={answered === total ? 'success' : 'secondary'}>
            {answered} / {total}
          </Badge>
        </div>
      )}
      <div className="space-y-2">
        {items.map((row) => (
          <ChecklistRow
            key={row.id}
            row={row}
            readOnly={readOnly}
            onAnswerChange={(answer) => deferCommit
              ? applyDraft(row.id, { answer })
              : update.mutate({ id: row.id, patch: { answer } })}
            onCommentsBlur={(comments) => {
              if (comments === (row.comments ?? '')) return;
              const patch = { comments: comments || null };
              if (deferCommit) applyDraft(row.id, patch);
              else update.mutate({ id: row.id, patch });
            }}
          />
        ))}
      </div>
    </div>
  );
});

function ChecklistRow({
  row, readOnly, onAnswerChange, onCommentsBlur,
}: {
  row: ChecklistItem;
  readOnly: boolean;
  onAnswerChange: (answer: string) => void;
  onCommentsBlur: (comments: string) => void;
}) {
  const [comments, setComments] = React.useState(row.comments ?? '');
  // Sync local state when the row's comments value changes from elsewhere (e.g. cache invalidation).
  React.useEffect(() => { setComments(row.comments ?? ''); }, [row.comments]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_1fr] gap-2 items-start border rounded-md p-3">
      <div className="text-sm">
        <div className="flex items-center gap-2">
          <AnswerIcon answer={row.answer} />
          <span className={row.answer ? '' : 'text-muted-foreground'}>{row.task || <em>untitled</em>}</span>
        </div>
      </div>
      <div>
        <Label className="text-xs text-muted-foreground sr-only">Answer</Label>
        <Select
          value={row.answer ?? ''}
          onValueChange={(v) => onAnswerChange(v)}
          disabled={readOnly}
        >
          <SelectTrigger className="h-9"><SelectValue placeholder="— Answer —" /></SelectTrigger>
          <SelectContent>
            {ANSWER_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-muted-foreground sr-only">Comments</Label>
        <Textarea
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          onBlur={(e) => onCommentsBlur(e.target.value)}
          placeholder="Comments…"
          disabled={readOnly}
          className="min-h-[36px] text-sm resize-y"
          rows={1}
        />
      </div>
    </div>
  );
}
